import React from 'react';
import { ChevronDown } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { alpha, createTheme } from '@mui/material/styles';
import type { Components, PaletteColor, Shadows, Theme } from '@mui/material/styles';
import { SCROLL_LOCK_IGNORE_COMPONENTS } from '../views/App/mui-theme';
import { ACCENT, CHEQUE, CPI, DISPLAY_STEPS, FONT_FAMILY, HUE, NEUTRAL, NOTE, RADIUS, SHADOW, STATUS } from './tokens';
import type { HueName, SoftDepthMode } from './tokens';

// The Soft Depth MUI theme. Mounted only inside <SoftDepthScope>, never
// globally: the legacy app theme (views/App/mui-theme.js) restores MUI v4
// behaviour (standard-variant inputs, colour-inheriting buttons, white
// textPrimary…) that every older screen depends on, and none of that belongs
// here. So this theme is built from scratch rather than layered on top.
//
// Sizes are written in px on purpose: html is 62.5% (1rem = 10px), and px reads
// the same as the design system's tokens with no conversion to get wrong.

declare module '@mui/material/styles' {
  interface PaletteColor {
    /** Pale fill behind selected / status content (accent-soft, success-soft…). */
    soft?: string;
    /** Outline of an accent-tinted panel or chip (accent-border). */
    border?: string;
  }
  interface SimplePaletteColorOptions {
    soft?: string;
    border?: string;
  }
  interface Palette {
    ai: PaletteColor;
    /** The adviser's own notes (paper yellow), not a status. */
    note: PaletteColor;
    /** 潛力指數 signal dots: `bgcolor: 'cpi.high'`. */
    cpi: { high: string; good: string; fair: string };
    /** Category hues: `bgcolor: 'hue.pink.soft'`, `color: 'hue.pink.main'`. */
    hue: Record<HueName, PaletteColor>;
    /** 支票版 paper and pen: `bgcolor: 'cheque.paper'`, `color: 'cheque.ink'`. */
    cheque: typeof CHEQUE;
  }
  interface PaletteOptions {
    ai?: SimplePaletteColorOptions;
    note?: SimplePaletteColorOptions;
    cpi?: { high: string; good: string; fair: string };
    hue?: Record<HueName, SimplePaletteColorOptions>;
    cheque?: typeof CHEQUE;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    mascot: React.CSSProperties;
    display1: React.CSSProperties;
    display2: React.CSSProperties;
    display3: React.CSSProperties;
    display4: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    mascot?: React.CSSProperties;
    display1?: React.CSSProperties;
    display2?: React.CSSProperties;
    display3?: React.CSSProperties;
    display4?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mascot: true;
    display1: true;
    display2: true;
    display3: true;
    display4: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    ai: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    ai: true;
  }
}

// The select icon slot: MUI hands it className (for position and the open
// flip); the chevron's own size stays 16 as everywhere else.
const SelectChevron = (props: LucideProps) =>
  React.createElement(ChevronDown, { size: 16, 'aria-hidden': true, ...props });

// Every display step reads the same; only its size changes.
const DISPLAY_LINE = {
  fontWeight: 600,
  color: NEUTRAL.inkStrong,
  fontVariantNumeric: 'tabular-nums' as const
};

type ColorKey = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'ai';

const statusColor = (status: { main: string; text: string; soft: string; border: string }) => ({
  main: status.main,
  dark: status.text,
  light: status.soft,
  soft: status.soft,
  border: status.border,
  contrastText: '#ffffff'
});

const HUE_COLORS = Object.fromEntries(Object.entries(HUE).map(([name, hue]) => [name, statusColor(hue)])) as Record<
  HueName,
  ReturnType<typeof statusColor>
>;

const buildTheme = (mode: SoftDepthMode): Theme => {
  const accent = ACCENT[mode];

  const shadows = [...createTheme().shadows] as Shadows;
  shadows[1] = SHADOW.card;
  shadows[2] = SHADOW.hover;
  shadows[8] = SHADOW.pop;

  return createTheme({
    palette: {
      primary: {
        main: accent.main,
        dark: accent.strong,
        light: accent.muted,
        soft: accent.soft,
        border: accent.border,
        contrastText: '#ffffff'
      },
      success: statusColor(STATUS.success),
      warning: statusColor(STATUS.warning),
      error: statusColor(STATUS.danger),
      ai: statusColor(STATUS.ai),
      note: statusColor(NOTE),
      cpi: CPI,
      hue: HUE_COLORS,
      cheque: CHEQUE,
      text: {
        primary: NEUTRAL.ink,
        secondary: NEUTRAL.inkMuted,
        disabled: NEUTRAL.inkFaint
      },
      background: {
        default: accent.page,
        paper: NEUTRAL.surface
      },
      divider: NEUTRAL.line,
      grey: {
        50: NEUTRAL.fillSoft,
        100: NEUTRAL.fill,
        200: NEUTRAL.lineSoft,
        300: NEUTRAL.line,
        400: NEUTRAL.inkFaint,
        500: NEUTRAL.inkMuted,
        600: NEUTRAL.inkSecondary,
        700: NEUTRAL.ink,
        800: NEUTRAL.inkHeading,
        900: NEUTRAL.inkStrong
      }
    },
    shape: { borderRadius: RADIUS.sm },
    shadows,
    typography: {
      htmlFontSize: 10,
      fontSize: 14,
      fontFamily: FONT_FAMILY,
      // stat — the figure in a stat tile
      h4: {
        fontSize: '22px',
        lineHeight: '28px',
        fontWeight: 700,
        color: NEUTRAL.inkStrong,
        fontVariantNumeric: 'tabular-nums'
      },
      // title — page and drawer titles
      h5: { fontSize: '18px', lineHeight: '26px', fontWeight: 700, color: NEUTRAL.inkStrong },
      // heading — card and dialog headings
      h6: { fontSize: '15px', lineHeight: '22px', fontWeight: 600, color: NEUTRAL.inkHeading },
      body1: { fontSize: '14px', lineHeight: '22px' },
      body2: { fontSize: '13px', lineHeight: '20px' },
      // label — field labels, chip and tab text
      subtitle2: { fontSize: '12px', lineHeight: '18px', fontWeight: 600 },
      caption: { fontSize: '11px', lineHeight: '16px', fontWeight: 500 },
      overline: { fontSize: '10.5px', lineHeight: '14px', fontWeight: 700, letterSpacing: '0.04em' },
      button: { fontSize: '13px', lineHeight: '20px', fontWeight: 600, textTransform: 'none' },
      // mascot — a persona card's big type emoji (🦅), the size it was before Soft Depth
      mascot: { fontSize: '44px', lineHeight: 1 },
      // display1…4 — the steps of a zoomable display line (DISPLAY_STEPS): the
      // cheque helper's payee name and amount in words, stepped with its A− / A+.
      // A screen picks a step, never a size, so a themed file still writes none.
      display1: { ...DISPLAY_STEPS[0], ...DISPLAY_LINE },
      display2: { ...DISPLAY_STEPS[1], ...DISPLAY_LINE },
      display3: { ...DISPLAY_STEPS[2], ...DISPLAY_LINE },
      display4: { ...DISPLAY_STEPS[3], ...DISPLAY_LINE }
    },
    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1340, xl: 1920 }
    },
    components: {
      // A data-* attribute is not in ModalProps / PopperProps, but MUI spreads
      // unknown defaultProps onto the root element, which is all this needs.
      ...(SCROLL_LOCK_IGNORE_COMPONENTS as Components<Theme>),
      MuiPaper: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: RADIUS.lg }
        }
      },
      // A card is radius-lg, but a menu, popover or autocomplete list is
      // radius-md with the pop shadow; their Papers would otherwise take the
      // card's 16px from MuiPaper above.
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: RADIUS.md, boxShadow: SHADOW.pop } }
      },
      MuiAutocomplete: {
        styleOverrides: { paper: { borderRadius: RADIUS.md, boxShadow: SHADOW.pop } }
      },
      MuiButton: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          // v9 has no containedPrimary / outlinedPrimary slots: gate on ownerState.
          root: ({ ownerState }) => ({
            borderRadius: RADIUS.sm,
            padding: '7px 16px',
            transition:
              'background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
            '&:active:not(.Mui-disabled)': { transform: 'scale(0.97)' },
            '&.Mui-focusVisible': { outline: `2px solid ${accent.main}`, outlineOffset: 2 },
            // The quiet button (`variant="outlined" color="inherit"`): a neutral
            // secondary action such as 重置 beside a contained primary one.
            ...(ownerState.color === 'inherit' &&
              ownerState.variant === 'outlined' && {
                color: NEUTRAL.inkSecondary,
                borderColor: NEUTRAL.line,
                backgroundColor: NEUTRAL.surface,
                '&:hover': { borderColor: accent.muted, backgroundColor: NEUTRAL.fillSoft, color: accent.deep }
              }),
            ...(ownerState.color === 'primary' &&
              ownerState.variant === 'contained' && {
                boxShadow: SHADOW.accent(mode),
                '&:hover': { backgroundColor: accent.deep, boxShadow: SHADOW.accent(mode) },
                '&:active': { boxShadow: SHADOW.accent(mode) }
              }),
            ...(ownerState.color === 'primary' &&
              ownerState.variant === 'outlined' && {
                color: accent.strong,
                borderColor: accent.border,
                '&:hover': { backgroundColor: accent.soft, borderColor: accent.border }
              })
          })
        }
      },
      // Chips are soft pills, never saturated blocks. As filter options:
      //   unselected  → variant="outlined" (no colour): grey pill on a line border
      //   selected    → variant="filled" color="primary": accent-soft + accent text
      // A coloured outlined chip (color="warning"…) is the unselected form of
      // that colour, its filled form the selected one. Every chip carries a 1px
      // border, so selecting one never shifts its neighbours.
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const color = ownerState.color as ColorKey | 'default' | undefined;
            const tint = color && color !== 'default' ? (theme.palette[color] as PaletteColor) : null;
            const outlined = ownerState.variant === 'outlined';
            const base = tint
              ? outlined
                ? { backgroundColor: NEUTRAL.surface, color: tint.dark, borderColor: tint.border ?? tint.light }
                : {
                    backgroundColor: tint.soft,
                    color: tint.dark,
                    borderColor: tint.border ?? tint.soft,
                    fontWeight: 700
                  }
              : outlined
              ? { backgroundColor: NEUTRAL.fillSoft, color: NEUTRAL.inkSecondary, borderColor: NEUTRAL.line }
              : { backgroundColor: NEUTRAL.fill, color: NEUTRAL.inkSecondary, borderColor: NEUTRAL.fill };
            // 28px for filter options, 24px (`size="small"`) for long runs of
            // names such as families and tags, where the panel's length is
            // made of chip rows.
            const small = ownerState.size === 'small';
            return {
              height: small ? 24 : 28,
              borderRadius: RADIUS.pill,
              border: '1px solid',
              fontSize: '12px',
              fontWeight: 600,
              ...base,
              '& .MuiChip-label': small ? { paddingLeft: 9, paddingRight: 9 } : { paddingLeft: 11, paddingRight: 11 },
              ...(ownerState.clickable && {
                transition:
                  'transform 120ms ease, border-color 120ms ease, background-color 120ms ease, color 120ms ease',
                // MUI darkens a clickable chip's fill on hover / focus; here the
                // fill means "selected or not", so only the border answers.
                '&:hover, &.Mui-focusVisible': { ...base, borderColor: accent.muted },
                '&.Mui-focusVisible': { ...base, outline: `2px solid ${accent.main}`, outlineOffset: 2 },
                '&:active': { transform: 'scale(0.93)', boxShadow: 'none' }
              })
            };
          }
        }
      },
      MuiPaginationItem: {
        defaultProps: { variant: 'outlined', shape: 'rounded' },
        styleOverrides: {
          root: ({ ownerState }) =>
            ownerState.type === 'start-ellipsis' || ownerState.type === 'end-ellipsis'
              ? { minWidth: 'auto', margin: 0, padding: '0 2px', color: NEUTRAL.inkFaint }
              : {
                  minWidth: 30,
                  height: 30,
                  margin: 0,
                  padding: '0 8px',
                  borderRadius: RADIUS.sm,
                  borderColor: NEUTRAL.line,
                  backgroundColor: NEUTRAL.surface,
                  color: NEUTRAL.inkSecondary,
                  fontSize: '12px',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  transition:
                    'transform 120ms ease, border-color 150ms ease, background-color 150ms ease, color 150ms ease',
                  '&:hover': { borderColor: accent.muted, color: accent.deep, backgroundColor: NEUTRAL.surface },
                  '&:active:not(.Mui-disabled)': { transform: 'scale(0.93)' },
                  '&.Mui-focusVisible': { outline: `2px solid ${accent.main}`, outlineOffset: 2 },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    borderColor: accent.strong,
                    backgroundColor: accent.strong,
                    color: '#ffffff'
                  },
                  '&.Mui-disabled': { opacity: 0.4 }
                }
        }
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            backgroundColor: NEUTRAL.fillSoft,
            fontSize: '13px',
            color: NEUTRAL.ink,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: NEUTRAL.line },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accent.muted },
            '&.Mui-focused': { backgroundColor: NEUTRAL.surface },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent.main, borderWidth: 2 }
          },
          input: {
            padding: '5px 8px',
            '&::placeholder': { color: NEUTRAL.inkFaint, opacity: 1 }
          },
          adornedStart: { paddingLeft: 8 }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: { color: NEUTRAL.inkMuted, fontWeight: 700, '& .MuiTypography-root': { fontSize: '12px' } },
          positionStart: { marginRight: 2 }
        }
      },
      // Selects use the lucide chevron like every other ClientV2 control
      // (`native` included, which keeps the phone's own picker).
      MuiSelect: {
        defaultProps: { IconComponent: SelectChevron },
        styleOverrides: { icon: { top: 'calc(50% - 8px)', right: 8, color: NEUTRAL.inkMuted } }
      },
      MuiNativeSelect: {
        defaultProps: { IconComponent: SelectChevron },
        styleOverrides: { icon: { top: 'calc(50% - 8px)', right: 8, color: NEUTRAL.inkMuted } }
      },
      // One rail, one thumb shape for every slider: a 4px line-coloured rail,
      // the strong accent for the filled part and a 15px thumb ringed in white.
      MuiSlider: {
        defaultProps: { valueLabelDisplay: 'off' },
        styleOverrides: {
          // 9px around the rail on a mouse; MUI's own 20px on a touch screen
          // (a ~42px target) is restated after it, or this override would win
          // there too and leave a phone a ~22px strip to hit.
          root: {
            height: 4,
            padding: '9px 0',
            color: accent.strong,
            '@media (pointer: coarse)': { padding: '20px 0' }
          },
          rail: { backgroundColor: NEUTRAL.line, opacity: 1 },
          track: { border: 'none' },
          thumb: {
            width: 15,
            height: 15,
            backgroundColor: accent.strong,
            border: `2.5px solid ${NEUTRAL.surface}`,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.35)',
            '&::before': { boxShadow: 'none' },
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 1px 3px rgba(15, 23, 42, 0.35), 0 0 0 6px ${alpha(accent.main, 0.16)}`
            },
            '&.Mui-active': {
              boxShadow: `0 1px 3px rgba(15, 23, 42, 0.35), 0 0 0 8px ${alpha(accent.main, 0.16)}`
            }
          }
        }
      }
    }
  });
};

const built: Partial<Record<SoftDepthMode, Theme>> = {};

/**
 * The theme for a mode, built on first use and kept: switching mode swaps the
 * theme object, nothing is recreated. Lazy because this module is in the main
 * bundle (ClientV2 is imported eagerly), and a session that never opens a Soft
 * Depth screen should not pay for building it.
 */
export const getSoftDepthTheme = (mode: SoftDepthMode): Theme => {
  if (!built[mode]) built[mode] = buildTheme(mode);
  return built[mode] as Theme;
};
